import { useState, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Building2, Calculator, Hammer, Layers, Ruler, FileText, AlertCircle } from 'lucide-react';

const constructionTypes = [
  { id: 'standard', name: 'Standard Home', rate: 35_000 },
  { id: 'midrange', name: 'Mid-Range Home', rate: 45_000 },
  { id: 'highend', name: 'High-End Home', rate: 60_000 },
  { id: 'multistory', name: 'Multi-Story High-End', rate: 75_000 },
];

function formatETB(amount: number) {
  return new Intl.NumberFormat('en-ET').format(Math.round(amount));
}

export default function CostCalculator() {
  const [typeId, setTypeId] = useState('standard');
  const [area, setArea] = useState(120);
  const [floors, setFloors] = useState(1);
  const [includeSoftCosts, setIncludeSoftCosts] = useState(true);

  const type = constructionTypes.find(t => t.id === typeId)!;

  const calc = useMemo(() => {
    const totalArea = area * floors;
    const baseCost = totalArea * type.rate;
    const softCosts = includeSoftCosts ? baseCost * 0.1 : 0;
    const totalCost = baseCost + softCosts;
    return { totalArea, baseCost, softCosts, totalCost };
  }, [area, floors, type.rate, includeSoftCosts]);

  return (
    <Layout>
      <div className="bg-background min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Construction Cost Calculator
            </h1>
            <span className="hidden sm:inline text-sm text-muted-foreground">
              ETB {formatETB(calc.totalCost)} Estimated Total
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left — Inputs */}
            <Card className="lg:col-span-3 p-6 space-y-8">
              <div className="flex items-center gap-3 mb-2">
                <Hammer className="h-8 w-8 text-primary" />
                <span className="font-semibold text-lg text-foreground">Construction Parameters</span>
              </div>

              {/* Construction Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Construction Type</label>
                <Select value={typeId} onValueChange={setTypeId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {constructionTypes.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} — ETB {formatETB(t.rate)}/m²
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Total Area */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">Floor Area</label>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-foreground">{area} m²</span>
                  <span className="text-muted-foreground">per floor</span>
                </div>
                <Slider
                  value={[area]}
                  onValueChange={v => setArea(v[0])}
                  min={40}
                  max={500}
                  step={10}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>40 m²</span>
                  <span>500 m²</span>
                </div>
              </div>

              {/* Floors & Soft Costs side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Number of Floors</label>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-foreground">{floors} Floor{floors > 1 ? 's' : ''}</span>
                    <span className="text-muted-foreground">{calc.totalArea} m² total</span>
                  </div>
                  <Slider
                    value={[floors]}
                    onValueChange={v => setFloors(v[0])}
                    min={1}
                    max={5}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1</span>
                    <span>5</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Include Soft Costs (10%)</label>
                  <p className="text-xs text-muted-foreground">Permits, design fees & contingency</p>
                  <div className="flex items-center gap-3 pt-1">
                    <Switch checked={includeSoftCosts} onCheckedChange={setIncludeSoftCosts} />
                    <span className="text-sm text-foreground">{includeSoftCosts ? 'Included' : 'Excluded'}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Right — Results */}
            <Card className="lg:col-span-2 p-6 flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-bold text-foreground mb-6">Cost Estimate</h2>

                <div className="space-y-6">
                  <ResultRow
                    icon={<Calculator className="h-5 w-5 text-primary" />}
                    label="Total Estimated Cost"
                    value={`ETB ${formatETB(calc.totalCost)}`}
                    highlight
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <ResultRow
                      icon={<Ruler className="h-5 w-5 text-primary" />}
                      label="Cost per m²"
                      value={`ETB ${formatETB(type.rate)}`}
                    />
                    <ResultRow
                      icon={<Layers className="h-5 w-5 text-primary" />}
                      label="Total Area"
                      value={`${calc.totalArea} m²`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <ResultRow
                      icon={<Building2 className="h-5 w-5 text-primary" />}
                      label="Base Construction"
                      value={`ETB ${formatETB(calc.baseCost)}`}
                    />
                    <ResultRow
                      icon={<FileText className="h-5 w-5 text-primary" />}
                      label="Soft Costs (10%)"
                      value={includeSoftCosts ? `ETB ${formatETB(calc.softCosts)}` : '—'}
                    />
                  </div>
                  <ResultRow
                    icon={<Hammer className="h-5 w-5 text-primary" />}
                    label="Construction Type"
                    value={type.name}
                  />
                </div>
              </div>

              <div className="mt-8 flex items-start gap-2 p-3 rounded-md bg-muted text-muted-foreground text-xs">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Estimates exclude land acquisition costs. Actual costs may vary based on location, materials, and market conditions.</span>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function ResultRow({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-muted flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`font-semibold ${highlight ? 'text-primary text-lg' : 'text-foreground text-sm'}`}>
          {value}
        </p>
      </div>
    </div>
  );
}
